import type { Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

/**
 * The review queue as last fetched, kept across pages so the drafts list is on
 * screen at once when the reviewer comes back from editing a scan.
 *
 * `isStale` is the invalidation: anything that changes the queue from elsewhere
 * (a confirm on the edit page, a bulk scan landing) marks it, and the drafts
 * controller reloads when it sees the mark. The cached groups stay visible meanwhile.
 */
export class ReviewQueueRepository extends ChangeNotifier {
  #status: LoadStatus = { kind: "idle" };
  #groups: readonly ScanReviewGroup[] = [];
  #unrecognised: readonly Scan[] = [];

  get unrecognised(): readonly Scan[] {
    return this.#unrecognised;
  }
  #stale = false;

  get status(): LoadStatus {
    return this.#status;
  }

  get groups(): readonly ScanReviewGroup[] {
    return this.#groups;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  setLoading(): void {
    this.#stale = false;
    this.#set({ kind: "loading" });
  }

  setGroups(groups: readonly ScanReviewGroup[], unrecognised: readonly Scan[] = []): void {
    this.#unrecognised = unrecognised;
    this.#groups = [...groups];
    this.#set({ kind: "loaded" });
  }

  setError(message: string): void {
    this.#set({ kind: "error", message });
  }

  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
